import { useState, useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BusinessActivity } from '../../types'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  activity: BusinessActivity | null
  basePath: string
}

export function BusinessActivityDialog({ open, onOpenChange, businessId, activity, basePath }: Props) {
  const [apiError, setApiError] = useState<string | null>(null)

  const { data, setData, post, patch, processing, reset, clearErrors } = useForm({
    name: ''
  })

  useEffect(() => {
    if (open) {
      if (activity) {
        setData({ name: activity.name })
      } else { reset() }
      setApiError(null); clearErrors()
    }
  }, [open, activity])

  function handleClose(success: boolean) {
    onOpenChange(false)
    if (!success) { reset(); clearErrors() }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setApiError(null)
    const resourcePath = `${basePath}/businesses/${businessId}/activities`
    if (activity?.id) {
      patch(`${resourcePath}/${activity.id}`, { onSuccess: () => handleClose(true), onError: (errors) => setApiError(Object.values(errors).join(', ')) })
    } else {
      post(resourcePath, { onSuccess: () => handleClose(true), onError: (errors) => setApiError(Object.values(errors).join(', ')) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader className="mb-6"><DialogTitle>{activity ? 'Edit Activity' : 'Add Activity'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 px-4">
          {apiError && <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3">{apiError}</div>}
          <div className="space-y-2">
            <Label htmlFor="activity_name">Activity Name <span className="text-destructive">*</span></Label>
            <Input
              id="activity_name"
              placeholder="e.g. Software Development"
              value={data.name}
              onChange={e => setData('name', e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-8">
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handleClose(false)}>Cancel</Button>
              <Button type="submit" className="flex-1" disabled={processing}>{processing ? 'Saving...' : activity ? 'Update' : 'Add Activity'}</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
